import { clamp } from '../core/math';

/** Pure positional-audio helpers (no Web Audio): distance attenuation, pan, filter colour. */

/** Listener = what the camera is centred on; `rx/rz` is the unit "screen right" on the ground. */
export interface Listener {
  x: number;
  z: number;
  rx: number;
  rz: number;
  vx: number;
  vz: number;
}

export function createListener(): Listener {
  return { x: 0, z: 0, rx: 1, rz: 0, vx: 0, vz: 0 };
}

/** Inverse-ish distance falloff: 1 at the listener, 0.5 at `ref` metres. */
export function distanceGain(distance: number, ref = 18): number {
  return 1 / (1 + Math.max(0, distance) / ref);
}

/**
 * Far fade on top of `distanceGain`, which never reaches zero: from the top-down camera a sound well off
 * screen should drop out rather than linger at -12 dB and clutter the mix. 1 up to `ref * 3.5`, 0 by
 * `ref * 6` (smoothstep).
 */
export function farFade(distance: number, ref = 18): number {
  const x = clamp((Math.max(0, distance) - ref * 3.5) / (ref * 2.5), 0, 1);
  return 1 - x * x * (3 - 2 * x);
}

/** Air absorption: lowpass cutoff (Hz) that closes with distance (never below 700 Hz). */
export function distanceCutoff(distance: number): number {
  return clamp(19000 / (1 + Math.max(0, distance) / 14), 700, 19000);
}

/** Stereo pan -1..1 from the offset to the source projected on screen-right (softened near the listener). */
export function panFor(dx: number, dz: number, rx: number, rz: number): number {
  const side = dx * rx + dz * rz;
  return clamp(side / 22, -1, 1) * 0.9;
}

/** Radial velocity (m/s, + = moving apart) of a source relative to the listener along the line of sight. */
export function radialVelocityAway(
  dx: number,
  dz: number,
  sourceVx: number,
  sourceVz: number,
  listenerVx: number,
  listenerVz: number,
): number {
  const d = Math.hypot(dx, dz);
  if (d < 0.5) return 0;
  return ((sourceVx - listenerVx) * dx + (sourceVz - listenerVz) * dz) / d;
}
